"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { logger } from "@/utils/logger";
import {
  diff_match_patch,
  DIFF_DELETE,
  DIFF_INSERT,
  DIFF_EQUAL,
} from "diff-match-patch";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import DOMPurify from "dompurify"; // Make sure to install this package

// Import necessary icons
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  HelpCircle,
  FileText,
  Info,
  ListChecks,
  Replace,
} from "lucide-react";

// --- Interfaces ---
/**
 * Frontend interface corresponding to backend's DetailedSuggestionSchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents a specific content update suggestion with original and updated text sections
 */
interface DetailedSuggestionSchema {
  original_section?: string | null;
  updated_section?: string | null;
}

/**
 * Frontend interface corresponding to backend's ReportEntrySchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents a single analysis entry comparing a release item to an article
 */
interface ReportEntrySchema {
  id: string;
  query_id: string;
  update_needed: boolean | null;
  relevance_score: number | null;
  reasoning?: string | null;
  general_suggestions: string[];
  detailed_suggestions: DetailedSuggestionSchema[]; // Array of DetailedSuggestionSchema objects from backend
  similarity_score: number | null;
  article_id: string;
  article_title: string;
  article_url?: string | null;
  article_description?: string | null;
  article_categories: string[];
  release_note_id: string;
  release_note_title: string;
  release_note_version?: string | null;
  release_note_url?: string | null;
  release_item_title: string;
  release_item_section_title?: string | null;
  release_item_version?: string | null;
  release_item_url?: string | null;
  release_item_description?: string | null;
  release_item_categories: string[];
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
}

/**
 * Frontend interface corresponding to backend's ReleaseItemDetailSchema Pydantic class
 * Path: data/api/api.py
 *
 * Represents complete details for a specific release item, including all related articles
 * Returned by GET /api/release-note/{note_id}/item/{item_id} endpoint
 */
interface ReleaseItemDetailSchema {
  id: string;
  note_id: string;
  note_title: string;
  note_version?: string | null;
  item_title: string;
  item_section_title?: string | null;
  item_version?: string | null;
  item_url?: string | null;
  item_description?: string | null;
  item_categories: string[];
  entries: ReportEntrySchema[]; // Array of ReportEntrySchema objects from backend
}

// Rename props interface
interface ReleaseItemProps {
  apiBaseUrl: string;
}

// Rename component function and props interface type
const ReleaseItem: React.FC<ReleaseItemProps> = ({ apiBaseUrl }) => {
  const { noteId, itemId } = useParams<{ noteId: string; itemId: string }>();
  const [itemDetails, setItemDetails] =
    useState<ReleaseItemDetailSchema | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<ReportEntrySchema | null>(
    null
  );
  const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
  const descriptionContainerRef = useRef<HTMLDivElement>(null); // Ref for description container
  const detailModalRef = useRef<HTMLDialogElement>(null); // Ref for detail modal
  const imageModalRef = useRef<HTMLDialogElement>(null); // Ref for image modal
  const { setBreadcrumbs } = useBreadcrumbs();

  // --- Fetch Details ---
  const fetchItemDetails = useCallback(async () => {
    if (!noteId || !itemId) {
      setError("Release Note ID or Item ID not found in URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // This endpoint needs to be implemented in your API
      const response = await axios.get<ReleaseItemDetailSchema>(
        `${apiBaseUrl}/release-note/${noteId}/item/${itemId}`
      );
      setItemDetails(response.data);
    } catch (err) {
      logger.error(`Error fetching details for item ${itemId}`, {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/release-note/${noteId}/item/${itemId}`,
        noteId,
        itemId,
      });
      let errorMsg = `Failed to load details for Release Item ${itemId}.`;
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 404) {
          errorMsg = `Release Item with ID '${itemId}' not found.`;
        } else {
          errorMsg = `API Error: ${err.response.data?.detail || err.message}`;
        }
      } else if (err instanceof Error) {
        errorMsg = `Error: ${err.message}`;
      }
      setError(errorMsg);
      setItemDetails(null);
    } finally {
      setLoading(false);
    }
  }, [noteId, itemId, apiBaseUrl]);

  useEffect(() => {
    fetchItemDetails();
  }, [fetchItemDetails]);

  // --- Open/Close Modals ---
  const openDetailModal = (entry: ReportEntrySchema) => {
    setSelectedEntry(entry);
    detailModalRef.current?.showModal();
  };

  const closeDetailModal = () => {
    detailModalRef.current?.close();
    setSelectedEntry(null);
  };

  const openImageModal = (url: string) => {
    setModalImageUrl(url);
    imageModalRef.current?.showModal();
  };

  const closeImageModal = () => {
    imageModalRef.current?.close();
    setModalImageUrl(null);
  };

  // --- Event Listener for Image Clicks in Description ---
  useEffect(() => {
    const container = descriptionContainerRef.current;

    const handleImageClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "IMG") {
        event.preventDefault();
        const imgElement = target as HTMLImageElement;
        openImageModal(imgElement.src); // Use modal opener function
      }
    };

    if (container) {
      container.addEventListener("click", handleImageClick);
    }

    // Cleanup function to remove the event listener
    return () => {
      if (container) {
        container.removeEventListener("click", handleImageClick);
      }
    };
  }, [itemDetails?.item_description]); // Re-run if description changes

  // --- Set Breadcrumbs ---
  useEffect(() => {
    if (itemDetails) {
      setBreadcrumbs([
        // Update first breadcrumb href
        { label: "Release Notes", href: "/release-notes" },
        {
          label: itemDetails.note_version
            ? `${itemDetails.note_version}`
            : itemDetails.note_title,
          // Update second breadcrumb href
          href: `/release-notes/${noteId}`,
        },
        { label: itemDetails.item_title },
      ]);
    } else if (loading) {
      setBreadcrumbs([
        // Update first breadcrumb href
        { label: "Release Notes", href: "/release-notes" },
        { label: "Loading..." },
      ]);
    } else {
      setBreadcrumbs([
        // Update first breadcrumb href
        { label: "Release Notes", href: "/release-notes" },
        { label: "Error Loading Details" },
      ]);
    }
  }, [setBreadcrumbs, itemDetails, loading, noteId]);

  // --- Rendering Helpers ---
  const renderStatusBadge = (status: boolean | null) => {
    if (status === true)
      return (
        <div className="badge badge-error gap-1 text-error-content">
          <AlertTriangle className="h-3.5 w-3.5" />
          Update Needed
        </div>
      );
    if (status === false)
      return (
        <div className="badge badge-success gap-1 text-success-content">
          <CheckCircle className="h-3.5 w-3.5" />
          No Update Needed
        </div>
      );
    return (
      <div className="badge badge-outline gap-1">
        <HelpCircle className="h-3.5 w-3.5" />
        Status Unknown
      </div>
    );
  };

  const renderScore = (score: number | null, precision = 2) => {
    if (score === null || score === undefined)
      return <span className="text-xs text-base-content/70">N/A</span>;

    let badgeClass = "badge";

    if (score >= 0.8) {
      badgeClass += " badge-success text-success-content";
    } else if (score >= 0.6) {
      badgeClass += " badge-warning text-warning-content";
    } else if (score >= 0.4) {
      badgeClass += " badge-info text-info-content";
    } else {
      badgeClass += " badge-error text-error-content";
    }

    return (
      <div className={badgeClass}>
        {score.toFixed(precision)}
      </div>
    );
  };

  // Calculate summary statistics for this item
  const calculateStats = () => {
    if (!itemDetails) return null;

    const totalEntries = itemDetails.entries.length;
    let updatesNeeded = 0;
    let totalRelevanceScore = 0;
    let totalSimilarityScore = 0;
    let relevanceCount = 0;
    let similarityCount = 0;
    let totalTokens = 0;

    itemDetails.entries.forEach((entry) => {
      if (entry.update_needed === true) updatesNeeded++;

      if (entry.relevance_score !== null) {
        totalRelevanceScore += entry.relevance_score;
        relevanceCount++;
      }

      if (entry.similarity_score !== null) {
        totalSimilarityScore += entry.similarity_score;
        similarityCount++;
      }

      if (entry.total_tokens) {
        totalTokens += entry.total_tokens;
      }
    });

    const avgRelevance =
      relevanceCount > 0 ? totalRelevanceScore / relevanceCount : null;
    const avgSimilarity =
      similarityCount > 0 ? totalSimilarityScore / similarityCount : null;
    const updatePercentage =
      totalEntries > 0 ? (updatesNeeded / totalEntries) * 100 : 0;

    return {
      totalEntries,
      updatesNeeded,
      avgRelevance,
      avgSimilarity,
      updatePercentage,
      totalTokens,
    };
  };

  const stats = calculateStats();

  // --- Loading States ---
  if (loading) {
    return (
      <div className="max-w-8xl mx-auto space-y-6 p-4 lg:p-6">
        <div className="card bg-base-200 shadow-md">
          <div className="card-body">
            <div className="skeleton h-8 w-3/4 mb-3"></div>
            <div className="skeleton h-4 w-1/4"></div>
            <div className="skeleton h-10 w-48 mt-4"></div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="card bg-base-200 shadow-md">
              <div className="card-body">
                <div className="skeleton h-6 w-32 mb-4"></div>
                <div className="space-y-2">
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-3/4"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-grow">
            <div className="card bg-base-200 shadow-md">
              <div className="card-body">
                <div className="skeleton h-6 w-48 mb-4"></div>
                <div className="space-y-4">
                  <div className="skeleton h-24 w-full"></div>
                  <div className="skeleton h-24 w-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Error States ---
  if (error) {
    return (
      <div role="alert" className="alert alert-error max-w-3xl mx-auto mt-8 shadow-lg">
        <AlertTriangle className="h-6 w-6" />
        <div>
          <h3 className="font-bold">Error</h3>
          <div className="text-xs">{error}</div>
        </div>
        <Link to={`/release-notes/${noteId}`} className="btn btn-sm btn-outline">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
      </div>
    );
  }

  if (!itemDetails) {
    return (
      <div className="card bg-base-200 shadow-md max-w-3xl mx-auto mt-8">
        <div className="card-body items-center text-center">
          <p className="text-base-content/70">
            Release item details could not be loaded.
          </p>
          <div className="card-actions justify-end mt-4">
            <Link to={`/release-notes/${noteId}`} className="btn btn-sm btn-outline">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // --- Main Detail Page Render ---
  return (
    <div className="max-w-8xl mx-auto">
      <style>{`
        .description-image-container img {
          cursor: pointer;
        }
        .diff-insert { background-color: hsl(var(--su) / 0.15); }
        .diff-delete { background-color: hsl(var(--er) / 0.15); text-decoration: line-through; }
      `}</style>
      <div className="flex flex-col lg:flex-row gap-8 p-4 lg:p-6">
        <aside className="w-full lg:w-1/3 xl:w-1/4 flex-shrink-0 space-y-6">
          <Link
            to={`/release-notes/${noteId}`}
            className="btn btn-sm btn-outline lg:hidden mb-4 w-full"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Release Note
          </Link>

          <h2 className="text-xl font-semibold px-1 flex items-center gap-2">
            {itemDetails.item_title}
          </h2>
          <div className="flex justify-between items-center gap-1 px-1">
            {itemDetails.note_version && (
              <div className="badge badge-neutral text-xs h-6">
                {itemDetails.note_version}
              </div>
            )}
            {itemDetails.item_url && (
              <a
                href={itemDetails.item_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link link-info link-hover text-xs inline-flex items-center"
              >
                Source <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            )}
            <Link
              to={`/release-notes/${noteId}`}
              title="Back to Release Note"
              className="btn btn-ghost btn-sm btn-square hidden lg:inline-flex ml-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
          <div className="card card-compact bg-base-200 shadow">
            <div className="card-body">
              {itemDetails.item_description && (
                <>
                  <h3 className="text-sm font-medium mb-1 text-base-content/70">
                    Description
                  </h3>
                  <div
                    ref={descriptionContainerRef}
                    className="prose prose-sm max-w-none text-sm description-image-container"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(itemDetails.item_description),
                    }}
                  />
                  {itemDetails.item_section_title && <div className="divider my-2"></div>}
                </>
              )}

              {itemDetails.item_section_title && (
                <div>
                  <h3 className="text-sm font-medium mb-1 text-base-content/70">
                    Section
                  </h3>
                  <p className="text-sm">
                    {itemDetails.item_section_title}
                  </p>
                </div>
              )}
            </div>
          </div>

          {stats && (
            <div className="card card-compact bg-base-200 shadow">
              <div className="card-body">
                <h2 className="card-title text-base">Analysis Summary</h2>
                <div className="space-y-2 text-sm pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-base-content/70">Articles:</span>
                    <span className="font-medium">{stats.totalEntries}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-base-content/70">Updates Needed:</span>
                    <span
                      className={`font-medium ${
                        stats.updatesNeeded > 0
                          ? "text-error"
                          : "text-success"
                      }`}
                    >
                      {stats.updatesNeeded}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-base-content/70">Avg. Relevance:</span>
                    <span className="font-medium">
                      {stats.avgRelevance !== null
                        ? stats.avgRelevance.toFixed(2)
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-base-content/70">Total Tokens:</span>
                    <span className="font-medium">
                      {stats.totalTokens?.toLocaleString() || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </aside>

        <main className="w-full lg:w-2/3 xl:w-3/4 space-y-6">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Affected Articles
          </h2>

          <div className="space-y-3">
            {itemDetails.entries.length > 0 ? (
              itemDetails.entries.map((entry) => (
                <div
                  key={entry.id}
                  onClick={() => openDetailModal(entry)}
                  className={`card card-compact shadow-sm cursor-pointer transition-all duration-150 ease-in-out ${
                    entry.update_needed === true
                      ? "bg-base-100 hover:bg-base-200 border border-base-300 hover:shadow-md"
                      : "bg-success/10 hover:bg-success/20 border border-success/30 hover:shadow-md"
                  }`}
                >
                  <div className="card-body p-3">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-grow">
                        <p className="font-medium text-sm mb-1">
                          {entry.article_title}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs items-center">
                          <div className="flex items-center gap-1">
                            <span className="text-base-content/70">
                              Relevance:
                            </span>
                            {renderScore(entry.relevance_score)}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-base-content/70">
                              Similarity:
                            </span>
                            {renderScore(entry.similarity_score)}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {renderStatusBadge(entry.update_needed)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="card bg-base-200 border border-base-300 border-dashed">
                <div className="card-body items-center text-center p-12">
                  <Info className="h-12 w-12 text-base-content/30 mx-auto mb-4" />
                  <p className="text-base-content/70">
                    No articles have been analyzed for this release item yet.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <dialog ref={detailModalRef} className="modal modal-bottom sm:modal-middle">
        <div className="modal-box w-11/12 max-w-4xl h-4/5 flex flex-col">
          {selectedEntry && (
            <>
              <div className="flex justify-between items-center pb-3 border-b border-base-300 mb-4">
                <div>
                  <h3 className="font-bold text-lg">{selectedEntry.article_title}</h3>
                  <div className="flex items-center gap-4 pt-1 text-sm">
                    {renderStatusBadge(selectedEntry.update_needed)}
                    {selectedEntry.article_url && (
                      <a
                        href={selectedEntry.article_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link link-info link-hover inline-flex items-center"
                      >
                        View Original Article{" "}
                        <ExternalLink className="ml-1 h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                <button className="btn btn-sm btn-circle btn-ghost" onClick={closeDetailModal}>✕</button>
              </div>

              <div className="flex-grow overflow-y-auto pr-2 space-y-4">
                <div className="card card-compact bg-base-200/50">
                  <div className="card-body p-3">
                    <h3 className="text-sm font-medium mb-2">Scores</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">Relevance:</span>
                        {renderScore(selectedEntry.relevance_score)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">Similarity:</span>
                        {renderScore(selectedEntry.similarity_score)}
                      </div>
                    </div>
                  </div>
                </div>

                {(selectedEntry.reasoning ||
                  (selectedEntry.general_suggestions &&
                    selectedEntry.general_suggestions.length > 0)) && (
                  <div className="card card-compact bg-base-200/50">
                    <div className="card-body p-3">
                      <h2 className="card-title text-base flex items-center gap-2 !p-0 !m-0">
                        <FileText className="h-4 w-4 text-primary" />
                        Analysis & Updates
                      </h2>
                      <div className="pt-2 text-sm space-y-3">
                        {selectedEntry.reasoning && (
                          <p>{selectedEntry.reasoning}</p>
                        )}
                        {selectedEntry.reasoning &&
                          selectedEntry.general_suggestions &&
                          selectedEntry.general_suggestions.length > 0 && (
                            <div className="divider my-1"></div>
                          )}
                        {selectedEntry.general_suggestions &&
                          selectedEntry.general_suggestions.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                                <ListChecks className="h-4 w-4 text-warning" />
                                Recommendations
                              </h4>
                              <ul className="list-disc list-inside space-y-1 pl-1">
                                {selectedEntry.general_suggestions.map(
                                  (suggestion, idx) => (
                                    <li key={idx}>{suggestion}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedEntry.detailed_suggestions &&
                  selectedEntry.detailed_suggestions.length > 0 && (
                    <div className="card card-compact bg-base-200/50">
                      <div className="card-body p-3">
                        <h2 className="card-title text-base flex items-center gap-2 !p-0 !m-0">
                          <Replace className="h-4 w-4 text-primary" />
                          Detailed Changes
                        </h2>
                        <div className="space-y-4 pt-2">
                          {selectedEntry.detailed_suggestions.map(
                            (detail, idx) => {
                              const dmp = new diff_match_patch();
                              const diffs = dmp.diff_main(
                                detail.original_section || "",
                                detail.updated_section || ""
                              );
                              dmp.diff_cleanupSemantic(diffs);

                              return (
                                <div
                                  key={idx}
                                  className="border-t border-base-300/50 pt-3 first:border-t-0 first:pt-0"
                                >
                                  <div className="p-2 bg-base-100 rounded border border-base-300/50 text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                                    <code>
                                      {diffs.map(([op, text], index) => {
                                        let className = "";
                                        if (op === DIFF_INSERT) {
                                          className = "diff-insert";
                                        } else if (op === DIFF_DELETE) {
                                          className = "diff-delete";
                                        } else {
                                          className = "text-base-content/80";
                                        }

                                        if (op === DIFF_EQUAL && text.trim() === "" && !text.includes('\n')) {
                                          return null;
                                        }

                                        return (
                                          <span
                                            key={index}
                                            className={className}
                                          >
                                            {text}
                                          </span>
                                        );
                                      })}
                                    </code>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeDetailModal}>close</button>
        </form>
      </dialog>

      <dialog ref={imageModalRef} className="modal">
        <div className="modal-box w-auto max-w-5xl max-h-[90vh] p-4 flex flex-col items-center justify-center">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" onClick={closeImageModal}>✕</button>
          </form>
          {modalImageUrl && (
            <img
              src={modalImageUrl}
              alt="Enlarged view"
              className="block object-contain max-w-full max-h-full"
            />
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeImageModal}>close</button>
        </form>
      </dialog>
    </div>
  );
};

// Update export
export default ReleaseItem;
